/**
 * Country normalization helpers - DEV-56352.
 *
 * The domestic/international shipping decision across this app is a single
 * string equality (`country === SHIP_FROM_COUNTRY`) whose `else` branch bills
 * the international rate. The two sides of that comparison are not guaranteed
 * to use the same representation:
 *
 *   - `country` is usually an ISO-2 code, read from `countries[label].value`,
 *     but falls back to the raw address string (a label) on a lookup miss.
 *   - `SHIP_FROM_COUNTRY` is whatever the brand-settings API returns. server.js
 *     assigns it with no default, no validation and no normalization.
 *
 * Any mismatch between the two silently bills the international rate. These
 * helpers collapse both sides to one ISO-2 representation so the comparison is
 * representation-safe regardless of which source supplied the value.
 *
 * Intentionally dependency-free and ASCII-only so it can be imported from both
 * `src/app/**` and `src/pages/**` without pulling in Angular DI.
 */

/**
 * Alias/label -> ISO-2 table.
 *
 * This is the ONLY resolution path available to `SHIP_FROM_COUNTRY`, which is
 * normalized at module load, before any `countries` map exists. So it must
 * cover every country this app is actually configured to sell from or to -
 * every `braintreeDefaultCountry` in `src/app/app-routing.module.ts` is
 * represented below. A ship-from label missing from this table cannot resolve
 * to ISO-2, and would be compared against destinations that DID resolve
 * through the map (e.g. 'GERMANY' vs 'DE'), re-creating the exact
 * false-international charge this change removes.
 *
 * Deliberately not the full 238-row set from `countries.json`: those labels are
 * percent-encoded at source ('COTE D%27IVOIRE', 'CURA%C3%A7AO') and decoding
 * them would introduce non-ASCII into a file the repo requires to stay
 * ASCII-safe. The runtime map covers the long tail at every call site that
 * passes it, and `normalizeLookupKey()` below decodes those keys on the way in.
 *
 * Two entries need explaining: 'PORTGUAL' is the misspelling used by the live
 * route at app-routing.module.ts:102, and 'CHINESE TAIPEI' is what
 * countries.json calls TW. 'MEXICO' and 'HONG KONG' are absent from
 * countries.json entirely, so the alias is their only resolution path.
 */
const COUNTRY_ALIASES: { [key: string]: string } = {
  'US': 'US',
  'USA': 'US',
  'U.S.': 'US',
  'U.S.A.': 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  'CA': 'CA',
  'CANADA': 'CA',
  'GB': 'GB',
  'UK': 'GB',
  'UNITED KINGDOM': 'GB',
  'GREAT BRITAIN': 'GB',
  'AU': 'AU',
  'AUSTRALIA': 'AU',
  'GERMANY': 'DE',
  'DEUTSCHLAND': 'DE',
  'FRANCE': 'FR',
  'MEXICO': 'MX',
  'BRAZIL': 'BR',
  'BRASIL': 'BR',
  'CHINA': 'CN',
  'HONG KONG': 'HK',
  'ICELAND': 'IS',
  'JAPAN': 'JP',
  'PORTUGAL': 'PT',
  'PORTGUAL': 'PT',
  'RUSSIA': 'RU',
  'RUSSIAN FEDERATION': 'RU',
  'SWITZERLAND': 'CH',
  'TAIWAN': 'TW',
  'CHINESE TAIPEI': 'TW',
};

/**
 * Upper-cased, percent-decoded form of a country key, for map lookups.
 * `countries.json` stores labels percent-encoded, and `SHIP_FROM_COUNTRY` has
 * already been upper-cased by the time it reaches the decision point, so an
 * exact case-sensitive index hit cannot be relied on.
 */
function normalizeLookupKey(key: string): string {
  let k = key.trim();
  if (k.indexOf('%') !== -1) {
    try {
      k = decodeURIComponent(k);
    } catch (e) {
      // malformed escape sequence - fall through with the raw value
    }
  }
  return k.toUpperCase();
}

/**
 * Build an upper-cased/decoded key -> ISO-2 index so the countries map resolves
 * regardless of casing or percent-encoding.
 *
 * Cached per map object, but the map is MUTABLE: callers commonly assign an
 * empty object and populate it once `getCountries()` resolves, and the
 * countries-map race in the test plan depends on exactly that. Caching on
 * object identity alone would pin the first (possibly empty) snapshot forever
 * and silently stop resolving. So the entry count is stored with the index and
 * the index is rebuilt whenever it changes.
 *
 * Not covered: an in-place edit that swaps a value without changing the key
 * count. That does not happen with this data and rebuilding on every call
 * would defeat the cache.
 */
const COUNTRY_INDEX_CACHE: any = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

function countryIndex(countries: any): { [key: string]: string } {
  const size = Object.keys(countries).length;
  if (COUNTRY_INDEX_CACHE && COUNTRY_INDEX_CACHE.has(countries)) {
    const cached = COUNTRY_INDEX_CACHE.get(countries);
    if (cached && cached.size === size) {
      return cached.index;
    }
  }
  const index: { [key: string]: string } = {};
  for (const key in countries) {
    if (Object.prototype.hasOwnProperty.call(countries, key)) {
      const entry = countries[key];
      if (entry && entry.value) {
        index[normalizeLookupKey(key)] = String(entry.value).trim().toUpperCase();
      }
    }
  }
  if (COUNTRY_INDEX_CACHE) {
    COUNTRY_INDEX_CACHE.set(countries, { size: size, index: index });
  }
  return index;
}

/** True when `value` is already a bare ISO-3166-1 alpha-2 code. */
export function isIsoCountryCode(value: string): boolean {
  return /^[A-Z]{2}$/.test(value);
}

/**
 * Collapse any country representation (ISO-2, display label, common alias) to
 * an upper-case ISO-2 code.
 *
 * Resolution order:
 *   1. Exact label hit in the `countries` map - authoritative, returns `.value`.
 *   2. Known alias table.
 *   3. Upper-cased input, so at minimum casing and padding never decide pricing.
 *
 * Step 3 is a best-effort fallback, NOT a guarantee: an unrecognised label such
 * as 'Deutschland' comes back as 'DEUTSCHLAND', which is plainly not an ISO-2
 * code. That is deliberate. This function is also reached via
 * `CalculateProvider.getCountryISOCode()`, which populates outbound API fields
 * (`billingCountryISOCode`, `shippingCountryISOCode`, `shipToCountry`,
 * `countryISO`). Returning '' for anything unrecognised would replace a
 * best-effort value with an empty one in those payloads - a data regression
 * well outside the scope of this fix. Callers that need certainty should test
 * the result with `isIsoCountryCode()`; `isDomesticShipping()` below does.
 *
 * Finite NUMBERS are coerced to their string form rather than discarded.
 * `CalculateProvider.getCountryISOCode()` is typed `country: string | number`,
 * and pre-fix it fed a numeric key straight into the `countries` lookup,
 * returning `.value` on a hit and the number itself on a miss. Dropping
 * numbers to '' here would regress that path - and, through the same helper,
 * would empty the outbound country fields listed above. Anything else
 * (null, undefined, NaN, boolean, object) is not a country and yields ''.
 *
 * Returns '' for empty / unusable input. Callers rely on that empty string
 * being falsy to preserve the existing `if (country)` guards.
 */
export function toIsoCountry(value: any, countries?: any): string {
  let raw = '';
  if (typeof value === 'string') {
    raw = value.trim();
  } else if (typeof value === 'number' && isFinite(value)) {
    raw = String(value);
  }
  if (!raw) {
    return '';
  }
  if (countries && countries[raw] && countries[raw].value) {
    return String(countries[raw].value).trim().toUpperCase();
  }
  const upper = raw.toUpperCase();
  if (countries) {
    const fromMap = countryIndex(countries)[normalizeLookupKey(raw)];
    if (fromMap) {
      return fromMap;
    }
  }
  return COUNTRY_ALIASES[upper] || upper;
}

/**
 * True when the destination country is the country the brand ships from.
 *
 * Both sides are normalized before comparison. Two distinct outcomes:
 *
 *   - Either side is EMPTY (missing / non-string): return false, i.e. the
 *     international branch. That matches today's behaviour rather than
 *     changing pricing as a side effect of this fix, and it warns so the case
 *     is diagnosable instead of silent.
 *   - Either side normalized to something that is NOT an ISO-2 code (an
 *     unrecognised label like 'DEUTSCHLAND'): warn, then still compare the
 *     normalized strings. Comparing is intentional - it is exactly what the
 *     pre-fix code did, so a brand shipping from an unmapped country keeps
 *     pricing its own market as domestic. Rejecting outright here would invent
 *     a NEW class of false-international charge, which is the very bug this
 *     change exists to remove. The warning is what makes the gap visible: the
 *     country belongs in COUNTRY_ALIASES or in the countries map.
 *
 * Whether an unresolvable country should instead hard-fail the order is a
 * product decision tracked as a follow-up on DEV-56352.
 */
/**
 * Emit the unresolved-country diagnostic.
 *
 * Exported because several callers guard with their own `if (country)` before
 * ever reaching `isDomesticShipping()` - `calculate.ts` sets `shippingCost = 0`
 * for an empty country and returns early - so the warning would never fire on
 * exactly the paths that most need it, including card checkout. Those callers
 * invoke this directly for validation while keeping their existing pricing.
 */
export function warnUnresolvedCountry(country: any, shipFromCountry: any, context?: string): void {
  console.warn('[GH] DEV-56352 unresolved country in shipping decision', {
    country: country,
    shipFromCountry: shipFromCountry,
    context: context || 'isDomesticShipping',
  });
}

export function isDomesticShipping(country: any, shipFromCountry: any, countries?: any): boolean {
  const to = toIsoCountry(country, countries);
  const from = toIsoCountry(shipFromCountry, countries);
  if (!to || !from) {
    warnUnresolvedCountry(country, shipFromCountry);
    return false;
  }
  if (!isIsoCountryCode(to) || !isIsoCountryCode(from)) {
    console.warn('[GH] DEV-56352 country did not resolve to an ISO-2 code; falling back to normalized string comparison', {
      country: country,
      normalizedCountry: to,
      shipFromCountry: shipFromCountry,
      normalizedShipFromCountry: from,
    });
  }
  return to === from;
}
